import React, { useEffect, useState } from 'react'
import moment from 'moment'

import Receive from '../../components/reusable/receive'
import { usePet } from '../../modules'

const money = value => Number(value ?? 0).toLocaleString()

const StatCard = ({ label, value }) => (
    <div className="rounded-sm border border-gray-200 bg-white px-4 py-3">
        <p className="text-xs uppercase text-gray-500 mb-1">{label}</p>
        <p className="text-lg font-semibold mb-0">KSh {money(value)}</p>
    </div>
)

const TopList = ({ title, rows, emptyLabel }) => (
    <div className="rounded-sm border border-gray-200 bg-white p-4">
        <h3 className="font-semibold mb-3">{title}</h3>
        {
            rows.length > 0 ?
                <table className="table-auto w-full">
                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                        <tr>
                            <th className="p-2 text-left">Name</th>
                            <th className="p-2 text-right">Outstanding</th>
                        </tr>
                    </thead>
                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                        {rows.map(row => (
                            <tr key={row.key}>
                                <td className="p-2">{row.name}</td>
                                <td className="p-2 text-right">KSh {money(row.totalOutstanding)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                :
                <p className="text-sm text-gray-500 mb-0">{emptyLabel}</p>
        }
    </div>
)

const Dashboard = () => {
    const range = [moment().startOf('month'), moment().endOf('day')]
    const asOf = moment()

    const [cashFlow, setCashFlow] = useState()
    const [arAging, setArAging] = useState()
    const [apAging, setApAging] = useState()
    const [profitAndLoss, setProfitAndLoss] = useState()
    const [insurerSummary, setInsurerSummary] = useState()

    const [{ loading: cashLoading, error: cashError }, loadCashFlow] = usePet(
        'financeCashFlow',
        { from: range[0]._d, to: range[1]._d },
        { notify: true, manual: true, callback: ({ data }) => setCashFlow(data) },
    )

    const [{ loading: arLoading, error: arError }, loadArAging] = usePet(
        'financeAging',
        { kind: 'ar', asOf: asOf._d, page: 1, limit: 5 },
        { notify: true, manual: true, callback: ({ data }) => setArAging(data) },
    )

    const [{ loading: apLoading, error: apError }, loadApAging] = usePet(
        'financeAging',
        { kind: 'ap', asOf: asOf._d, page: 1, limit: 5 },
        { notify: true, manual: true, callback: ({ data }) => setApAging(data) },
    )

    const [{ loading: plLoading, error: plError }, loadProfitAndLoss] = usePet(
        'financeProfitAndLoss',
        { from: range[0]._d, to: range[1]._d },
        { notify: true, manual: true, callback: ({ data }) => setProfitAndLoss(data) },
    )

    const [{ loading: insurerLoading, error: insurerError }, loadInsurerSummary] = usePet(
        'insurerArSummary',
        { page: 1, limit: 1 },
        { notify: true, manual: true, callback: ({ data }) => setInsurerSummary(data) },
    )

    useEffect(() => {
        loadCashFlow()
        loadArAging()
        loadApAging()
        loadProfitAndLoss()
        loadInsurerSummary()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const loading = cashLoading || arLoading || apLoading || plLoading || insurerLoading
    const error = cashError || arError || apError || plError || insurerError

    const insurerTotal = (insurerSummary?.summaries ?? []).reduce((sum, row) => sum + Number(row?.totalOutstanding ?? 0), 0)

    const topReceivables = (arAging?.rows ?? [])
        .slice()
        .sort((a, b) => Number(b?.totalOutstanding ?? 0) - Number(a?.totalOutstanding ?? 0))
        .slice(0, 5)
        .map(row => ({ key: `${row.subjectType}-${row.subject}`, name: row.displayName, totalOutstanding: row.totalOutstanding }))

    const topInsurers = (insurerSummary?.summaries ?? [])
        .slice(0, 5)
        .map(row => ({ key: row.insurer, name: row.name, totalOutstanding: row.totalOutstanding }))

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-semibold text-gray-800 mb-1">Dashboard</h1>
                <p className="text-sm text-gray-500 mb-0">Month-to-date snapshot as of {asOf.format('DD MMM YYYY')}.</p>
            </div>

            <Receive loading={loading} error={error} progress>
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                        <StatCard label="Cash on Hand" value={cashFlow?.totals?.closingCash} />
                        <StatCard label="Accounts Receivable" value={arAging?.totals?.totalOutstanding} />
                        <StatCard label="Accounts Payable" value={apAging?.totals?.totalOutstanding} />
                        <StatCard label="Insurer / SHA Receivables" value={insurerTotal} />
                        <StatCard label="Net Income (MTD)" value={profitAndLoss?.totals?.netIncome} />
                    </div>

                    <div className="mb-2">
                        <h3 className="font-semibold mb-3">Receivables Aging</h3>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                            {['current', '30', '60', '90', '90+'].map(bucket => (
                                <StatCard key={bucket} label={bucket === 'current' ? 'Current' : `${bucket} days`} value={arAging?.totals?.[bucket]} />
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                        <TopList title="Top Overdue (Patients & Suppliers)" rows={topReceivables} emptyLabel="No outstanding receivables." />
                        <TopList title="Top Insurer / SHA Balances" rows={topInsurers} emptyLabel="No outstanding insurer balances." />
                    </div>
                </>
            </Receive>
        </div>
    )
}

export default Dashboard
